import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle2, Cpu, Zap, Bot, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { SHOPIFY_INSTALL_URL } from './Navbar';

const HERO_IMAGE = '/assets/home-hero.png';

const HeroSection = () => {
  const features = [
    'AI-Powered Sales',
    '24/7 Automation',
    'Smart Recommendations'
  ];

  return (
    <section className="relative pt-24 sm:pt-28 lg:pt-32 pb-16 sm:pb-20 overflow-hidden bg-gradient-to-b from-slate-50 to-white" data-testid="hero-section">
      {/* Subtle Background Pattern */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(13,148,136,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(13,148,136,0.03)_1px,transparent_1px)] bg-[size:60px_60px]" />
      
      {/* Soft Glows */}
      <div className="absolute top-20 left-10 w-[300px] sm:w-[400px] h-[300px] sm:h-[400px] bg-teal-200/20 rounded-full blur-[100px]" />
      <div className="absolute bottom-20 right-10 w-[250px] sm:w-[350px] h-[250px] sm:h-[350px] bg-emerald-200/20 rounded-full blur-[100px]" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          {/* Left - Content */}
          <div className="text-left">
            {/* Badge */}
            <motion.div 
              className="inline-flex items-center gap-2 bg-teal-50 border border-teal-200 rounded-full px-3 sm:px-4 py-2 mb-6 sm:mb-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <Cpu className="w-4 h-4 text-teal-600" />
              <span className="text-xs sm:text-sm font-semibold text-teal-700">AI-Powered E-commerce Agents</span>
            </motion.div>

            {/* Main Headline */}
            <motion.h1 
              className="text-4xl sm:text-5xl lg:text-6xl font-black text-slate-900 leading-tight mb-5 sm:mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              AI Agents That{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-600 to-emerald-500">
                Sell for You
              </span>{' '}
              24/7
            </motion.h1>

            {/* Subheadline */}
            <motion.p 
              className="text-base sm:text-lg text-slate-600 leading-relaxed mb-8 sm:mb-10 max-w-xl"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              Arismate deploys intelligent AI agents across your e-commerce store to automate conversations, boost conversions, and scale revenue.
            </motion.p>

            {/* CTA Buttons */}
            <motion.div 
              className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-8 sm:mb-10"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              <a href={SHOPIFY_INSTALL_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto">
                <Button 
                  size="lg" 
                  className="w-full sm:w-auto bg-teal-600 hover:bg-teal-700 text-white font-bold text-base px-7 py-6 shadow-xl shadow-teal-600/20 group"
                  data-testid="hero-install-btn"
                >
                  <Zap className="w-5 h-5 mr-2" />
                  Install on Shopify
                  <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </a>
              <Link to="/contact" className="w-full sm:w-auto">
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="w-full sm:w-auto border-2 border-slate-300 text-slate-700 hover:text-teal-600 hover:border-teal-500 hover:bg-teal-50 font-bold text-base px-7 py-6 group"
                  data-testid="hero-contact-btn"
                >
                  Contact Us
                </Button>
              </Link>
            </motion.div>

            {/* Trust Features */}
            <motion.div 
              className="flex flex-wrap items-center gap-4 sm:gap-6"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
            >
              {features.map((feature) => (
                <div 
                  key={feature}
                  className="flex items-center gap-2 text-sm text-slate-600"
                >
                  <CheckCircle2 className="w-4 h-4 text-teal-500" />
                  <span className="font-medium">{feature}</span>
                </div>
              ))}
            </motion.div>
          </div>

          {/* Right - Hero Image + Floating Cards */}
          <motion.div
            className="relative"
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4, duration: 0.6 }}
          >
            <div className="relative">
              {/* Main Image */}
              <img 
                src={HERO_IMAGE}
                alt="Arismate AI chatbot on e-commerce store showing product recommendations"
                className="rounded-3xl shadow-2xl w-full h-[350px] sm:h-[450px] object-contain border-2 border-white bg-slate-50"
                data-testid="hero-image"
              />

              {/* Floating Stats Card */}
              <motion.div 
                className="absolute -bottom-5 -left-4 sm:-left-6 bg-white rounded-2xl shadow-xl p-4 sm:p-5 border border-slate-100"
                animate={{ y: [0, -6, 0] }}
                transition={{ repeat: Infinity, duration: 3 }}
              >
                <div className="flex items-center gap-3 sm:gap-4">
                  <div className="w-12 sm:w-14 h-12 sm:h-14 bg-teal-50 rounded-xl flex items-center justify-center border border-teal-200">
                    <Bot className="w-6 sm:w-7 h-6 sm:h-7 text-teal-600" />
                  </div>
                  <div>
                    <p className="text-2xl sm:text-3xl font-extrabold text-slate-900">+30%</p>
                    <p className="text-sm text-slate-500 font-medium">Conversions</p>
                  </div>
                </div>
              </motion.div>

              {/* AI Active Badge */}
              <motion.div 
                className="absolute -top-3 -right-2 sm:-right-4 bg-teal-600 text-white px-4 py-2 rounded-xl text-sm font-bold shadow-lg flex items-center gap-2"
                animate={{ y: [0, -5, 0] }}
                transition={{ repeat: Infinity, duration: 2.5 }}
              >
                <Sparkles className="w-4 h-4" />
                AI Active
              </motion.div>

              {/* Revenue Card */}
              <motion.div 
                className="absolute top-1/2 -right-3 sm:-right-6 bg-white rounded-xl shadow-lg p-3 sm:p-4 border border-slate-100"
                animate={{ y: [0, 5, 0] }}
                transition={{ repeat: Infinity, duration: 2.8 }}
              >
                <p className="text-xs text-slate-500 font-medium">Revenue Today</p>
                <p className="text-lg sm:text-xl font-extrabold text-emerald-600">$4,280</p>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
